import { useTranslation } from 'react-i18next';
import { Attractions } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';

import { TourProduct, tourPricePresentation } from '../tours';
import { useAudience } from '../audience';
import { formatMinutes } from '../format';
import { PriceBlock } from './PriceBlock';
import { MetaLine } from './MetaLine';
import { ResultMedia } from './ResultMedia';
import { RuteroResultCard } from './RuteroResultCard';

interface Props {
  product: TourProduct;
  disabled?: boolean;
  onOpen: (product: TourProduct) => void;
  /** Posición en la lista de resultados: da el escalonado de la entrada. */
  index?: number;
}

/**
 * Card de catálogo: describe un producto y abre su ficha. Fechas, tarifas,
 * sesiones y hold pertenecen al paso posterior, no a la lista comparativa.
 */
export function TourProductCard({ product, disabled, onOpen, index }: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  const pricePresentation = tourPricePresentation(product.offer);
  const duration = product.durationDays
    ? t('travelServices.durationDays', { count: product.durationDays })
    : formatMinutes(product.durationMinutes);
  const facts = [
    product.category,
    duration,
    product.languages?.length ? product.languages.join(' · ') : undefined,
    product.location,
    product.attributes?.freeCancellation
      ? t('travelServices.tours.freeCancellation')
      : undefined,
    product.attributes?.instantConfirmation
      ? t('travelServices.tours.instantConfirmation')
      : undefined,
    product.attributes?.mobileVoucher
      ? t('travelServices.tours.mobileVoucher')
      : undefined,
    product.attributes?.availabilitySummary,
  ];

  return (
    <RuteroResultCard
      index={index}
      media={
        <ResultMedia
          photo={product.image}
          icon={<Attractions sx={{ fontSize: 44 }} color="action" />}
          alt={product.title}
        />
      }
      header={
        isAgent ? (
          <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
            {product.provider}
          </Typography>
        ) : undefined
      }
      title={
        <Typography sx={{ fontSize: 15.5, fontWeight: 600 }}>
          {product.title}
        </Typography>
      }
      details={
        <>
          <MetaLine items={facts} />
          {product.description && (
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 12.5,
                mt: 0.75,
                whiteSpace: 'pre-line',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {product.description}
            </Typography>
          )}
        </>
      }
      price={
        <PriceBlock
          pricing={product.fromPricing}
          fromLabel={
            pricePresentation.showFrom
              ? t('travelServices.tours.from')
              : undefined
          }
          unit={
            pricePresentation.unit
              ? t(`travelServices.tours.${pricePresentation.unit}`)
              : undefined
          }
        />
      }
      action={
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => onOpen(product)}
        >
          {t('travelServices.tours.viewExperience')}
        </Button>
      }
    />
  );
}
